// ── Configurable weight constants ─────────────────────────────────────────────
export const LOCATION_NEIGHBORHOOD_MAX = 30;
export const TRANSPORTATION_ENVIRONMENT_MAX = 15;
export const PROPERTY_QUALITY_MAX = 55;

// ── Shared interfaces ──────────────────────────────────────────────────────────
export interface PropertyForScoring {
  propertyType?: string;
  propertyAge?: string;
  propertyCondition?: string;
  size?: number;
  rooms?: number;
  bathrooms?: number;
  floor?: number | string;
  parkingSpots?: number;
  hasElevator?: boolean;
  hasSaferoom?: boolean;
  hasBalcony?: boolean;
  balconySize?: number | null;
  view?: string;
  airDirection?: string;
  airDirectionLabel?: string;
  nearbyPlaces?: Record<string, unknown[]>;
}

export interface NeighborhoodScoreRow {
  location_score: number;
  confidence?: string | null;
  location_level?: string | null;
  reason?: string | null;
  strengths?: string[] | null;
  weaknesses?: string[] | null;
}

export interface NeighborhoodStatsRow {
  avg_price_per_sqm?: number | null;
  transactions_count?: number | null;
  data_period?: string | null;
  avg_price_per_sqm_transactions?: number | null;
  median_price_per_sqm_transactions?: number | null;
  transactions_count_12m?: number | null;
  transactions_stats_period?: string | null;
  calculated_from_transactions_at?: string | null;
  active_listings_count?: number | null;
  active_listings_updated_at?: string | null;
}

export interface ScoreBreakdownItem {
  parameter: string;
  description: string;
  score: number;
  maxScore: number;
}

// ── Quality score breakdown ────────────────────────────────────────────────────
export interface QualityBreakdownItem {
  parameter: string;
  score: number;
  maxScore: number;
}

export interface PropertyQualityResult {
  qualityScore: number;        // 0-100 internal
  qualityLabel: string;
  qualityComponent: number;    // 0-55 contribution to overall
  breakdown: QualityBreakdownItem[];
}

// ── Overall score result ───────────────────────────────────────────────────────
export interface PropertyScoreResult {
  totalScore: number;
  scoreLabel: 'מצוין' | 'טוב' | 'בינוני' | 'חלש';
  breakdown: ScoreBreakdownItem[];           // 3 top-level rows
  qualityResult: PropertyQualityResult;
  strengths: string[];
  weaknesses: string[];
  missingFields: string[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function clamp(v: number, min: number, max: number) { return Math.max(min, Math.min(max, v)); }

function getScoreLabel(score: number): PropertyScoreResult['scoreLabel'] {
  if (score >= 80) return 'מצוין';
  if (score >= 65) return 'טוב';
  if (score >= 45) return 'בינוני';
  return 'חלש';
}

function getQualityLabel(score: number): string {
  if (score >= 90) return 'איכות גבוהה מאוד';
  if (score >= 80) return 'איכות גבוהה';
  if (score >= 70) return 'איכות טובה';
  if (score >= 60) return 'איכות ממוצעת';
  if (score >= 50) return 'איכות נמוכה';
  return 'איכות נמוכה מאוד';
}

// ── Component 1: Location / Neighborhood — max 30 ─────────────────────────────
// GPT score originally out of 23; we normalise to 30.
function scoreLocation(ns: NeighborhoodScoreRow | null): { score: number; description: string; missing: boolean } {
  if (!ns) {
    // neutral: ~53% of 30
    return { score: 16, description: 'ציון ניטרלי — אין נתוני שכונה', missing: true };
  }
  const normalised = Math.round((ns.location_score / 23) * LOCATION_NEIGHBORHOOD_MAX);
  const score = clamp(normalised, 0, LOCATION_NEIGHBORHOOD_MAX);
  const level = ns.location_level ? `${ns.location_level}` : '';
  const reason = ns.reason ? ` — ${ns.reason}` : '';
  return {
    score,
    description: `${level}${reason}`.trim() || `ציון ${ns.location_score}/23`,
    missing: false,
  };
}

// ── Component 2: Transportation / Environment — max 15 ────────────────────────
function scoreNearbyPlaces(np?: Record<string, unknown[]>): { score: number; description: string; missing: boolean } {
  if (!np || Object.keys(np).length === 0) {
    return { score: 10, description: 'נתוני סביבה לא זמינים (ניטרלי)', missing: true };
  }
  const total = Object.values(np).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
  const cats = Object.keys(np).length;
  let score = 8;
  if (total >= 20) score = 14;
  else if (total >= 12) score = 12;
  else if (total >= 6) score = 10;
  if (cats >= 5) score = Math.min(TRANSPORTATION_ENVIRONMENT_MAX, score + 1);
  return { score: clamp(score, 0, TRANSPORTATION_ENVIRONMENT_MAX), description: `${total} מקומות ב-${cats} קטגוריות`, missing: false };
}

// ── Component 3: Property Quality — internal 0-100, scaled to 55 ──────────────

// Legacy age-value map for backward compatibility
const LEGACY_AGE_SCORES: Record<string, number> = {
  '0-2': 12, '3-8': 10.5, '9-15': 9, '16-25': 7, '26-45': 5.5, '46+': 2.5,
};

const NEW_AGE_SCORES: Record<string, number> = {
  new_0_5: 12, pinui_binui: 12, tama38_2: 12,
  years_6_10: 10.5, tama38_1: 9.5, years_11_20: 9,
  years_21_30: 7, years_31_40: 5.5, years_41_50: 4,
  years_50_plus: 2.5,
};

// 3a. Property Type — max 15
function qPropertyType(t?: string): QualityBreakdownItem {
  let score = 9;
  if (t) {
    const lc = t.toLowerCase();
    if (lc.includes('פנטהאוז') && !lc.includes('מיני')) score = 15;
    else if (lc.includes('מיני פנטהאוז') || lc === 'penthouse') score = 14;
    else if (lc.includes('דירת גן') || lc === 'garden_apartment') score = 13;
    else if (lc.includes('דופלקס') || lc === 'duplex') score = 12;
    else if (lc.includes('דירה') || lc === 'apartment') score = 10;
    else if (lc.includes('penthouse')) score = 15;
    else if (lc.includes('mini_penthouse')) score = 14;
    else score = 9;
  }
  return { parameter: 'סוג נכס', score: clamp(score, 0, 15), maxScore: 15 };
}

// 3b. Property Age — max 12
function qPropertyAge(raw?: string): QualityBreakdownItem {
  const score = raw
    ? (NEW_AGE_SCORES[raw] ?? LEGACY_AGE_SCORES[raw] ?? 6)
    : 6;
  return { parameter: 'גיל ומצב בניין', score: clamp(score, 0, 12), maxScore: 12 };
}

// 3c. Property Condition — max 10
const CONDITION_SCORES: Record<string, number> = {
  renovated_high: 10, renovated: 8, good: 6, original: 3, needs_renovation: 0, unknown: 5,
};
function qPropertyCondition(c?: string): QualityBreakdownItem {
  const score = c ? (CONDITION_SCORES[c] ?? 5) : 5;
  return { parameter: 'מצב הנכס', score: clamp(score, 0, 10), maxScore: 10 };
}

// 3d. Floor + Elevator — max 15
function qFloorElevator(floorRaw?: number | string, hasElevator?: boolean): QualityBreakdownItem {
  const floor = floorRaw !== undefined && floorRaw !== null ? Number(floorRaw) : NaN;
  if (isNaN(floor)) return { parameter: 'קומה ומעלית', score: 8, maxScore: 15 };
  let score: number;
  if (hasElevator) {
    if (floor >= 6) score = 15;
    else if (floor >= 3) score = 13;
    else if (floor >= 1) score = 11;
    else score = 9;
  } else {
    if (floor <= 2) score = 9;
    else if (floor === 3) score = 6;
    else score = 2;
  }
  return { parameter: 'קומה ומעלית', score: clamp(score, 0, 15), maxScore: 15 };
}

// 3e. Parking / Saferoom / Balcony — max 18
function qAmenitiesCombo(
  parkingSpots?: number,
  hasSaferoom?: boolean,
  hasBalcony?: boolean,
  balconySize?: number | null,
): QualityBreakdownItem {
  let score = 0;
  const p = parkingSpots ?? 0;
  if (p >= 2) score += 7;
  else if (p === 1) score += 5;
  if (hasSaferoom) score += 5;
  if (hasBalcony) {
    const bs = balconySize ?? 0;
    if (bs >= 20) score += 6;
    else if (bs >= 10) score += 5;
    else score += 4;
  }
  return { parameter: 'חניה / ממ"ד / מרפסת', score: clamp(score, 0, 18), maxScore: 18 };
}

// 3f. View + Air Direction — max 10
const VIEW_SCORES: Record<string, number> = { sea: 6, park: 5, open: 4, city: 3 };
function qViewAir(view?: string, airLabel?: string): QualityBreakdownItem {
  const viewScore = view ? (VIEW_SCORES[view] ?? 0) : 0;
  let airScore = 0;
  if (airLabel) {
    const dirs = (airLabel.match(/צפון|דרום|מזרח|מערב/g) || []).length;
    if (dirs >= 3) airScore = 4;
    else if (dirs === 2) airScore = 3;
    else if (dirs === 1) airScore = 1.5;
  }
  return { parameter: 'נוף וכיווני אוויר', score: clamp(viewScore + airScore, 0, 10), maxScore: 10 };
}

// 3g. Size / Rooms / Bathrooms — max 20
function qSizeRoomsBaths(size?: number, rooms?: number, bathrooms?: number): QualityBreakdownItem {
  // Planning score — max 8
  let planning = 4;
  if (size && rooms) {
    const sqmPerRoom = size / rooms;
    if (sqmPerRoom >= 22 && sqmPerRoom <= 35) planning = 8;
    else if ((sqmPerRoom >= 18 && sqmPerRoom < 22) || (sqmPerRoom > 35 && sqmPerRoom <= 45)) planning = 6;
    else if (sqmPerRoom < 18) planning = 3;
    else planning = 4; // >45
  }
  // Bathrooms score — max 6
  const baths = bathrooms ?? 0;
  const bathScore = baths >= 3 ? 6 : baths === 2 ? 5 : baths === 1 ? 3 : 2;
  // Size score — max 6
  let sizeScore = 2;
  if (size) {
    if (size >= 120) sizeScore = 6;
    else if (size >= 90) sizeScore = 5;
    else if (size >= 70) sizeScore = 4;
    else if (size >= 50) sizeScore = 3;
    else sizeScore = 2;
  }
  return { parameter: 'שטח ותכנון', score: clamp(planning + bathScore + sizeScore, 0, 20), maxScore: 20 };
}

// ── Main quality calculator ────────────────────────────────────────────────────
export function calculatePropertyQualityScore(property: PropertyForScoring): PropertyQualityResult {
  const airLabel = property.airDirectionLabel ?? property.airDirection ?? '';

  const items: QualityBreakdownItem[] = [
    qPropertyType(property.propertyType),
    qPropertyAge(property.propertyAge),
    qPropertyCondition(property.propertyCondition),
    qFloorElevator(property.floor, property.hasElevator),
    qAmenitiesCombo(property.parkingSpots, property.hasSaferoom, property.hasBalcony, property.balconySize),
    qViewAir(property.view, airLabel),
    qSizeRoomsBaths(property.size, property.rooms, property.bathrooms),
  ];

  const raw = items.reduce((s, i) => s + i.score, 0);
  const qualityScore = clamp(Math.round(raw), 0, 100);
  const qualityComponent = Math.round((qualityScore / 100) * PROPERTY_QUALITY_MAX);

  return {
    qualityScore,
    qualityLabel: getQualityLabel(qualityScore),
    qualityComponent,
    breakdown: items,
  };
}

// ── Main combined score calculator ────────────────────────────────────────────
export function calculatePropertyScore(
  property: PropertyForScoring,
  neighborhoodScore: NeighborhoodScoreRow | null,
  neighborhoodStats: NeighborhoodStatsRow | null,
): PropertyScoreResult {
  const loc = scoreLocation(neighborhoodScore);
  const transport = scoreNearbyPlaces(property.nearbyPlaces);
  const quality = calculatePropertyQualityScore(property);

  const totalScore = clamp(
    Math.round(loc.score + transport.score + quality.qualityComponent),
    0,
    100,
  );

  console.log('[propertyScoreEngine] scores debug:', {
    locationScore: loc.score,
    transportScore: transport.score,
    qualityComponent: quality.qualityComponent,
    qualityScore: quality.qualityScore,
    overallScore: totalScore,
  });

  const breakdown: ScoreBreakdownItem[] = [
    {
      parameter: 'מיקום ושכונה',
      description: loc.description,
      score: loc.score,
      maxScore: LOCATION_NEIGHBORHOOD_MAX,
    },
    {
      parameter: 'תחבורה וסביבה',
      description: transport.description,
      score: transport.score,
      maxScore: TRANSPORTATION_ENVIRONMENT_MAX,
    },
    {
      parameter: 'איכות הנכס',
      description: `${quality.qualityLabel} — מבוסס על סוג נכס, גיל ומצב, קומה, מעלית, חניה, ממ"ד, מרפסת, נוף ותכנון.`,
      score: quality.qualityComponent,
      maxScore: PROPERTY_QUALITY_MAX,
    },
  ];

  const missingFields: string[] = [];
  if (loc.missing) missingFields.push('ציון שכונה');
  if (transport.missing) missingFields.push('נתוני סביבה');
  if (!property.propertyCondition) missingFields.push('מצב הנכס');
  if (!property.size) missingFields.push('שטח / חדרים');

  const strengths: string[] = [];
  const weaknesses: string[] = [];

  if (loc.score >= Math.round(LOCATION_NEIGHBORHOOD_MAX * 0.75)) strengths.push('מיקום מעולה');
  else if (loc.score <= Math.round(LOCATION_NEIGHBORHOOD_MAX * 0.45)) weaknesses.push('מיקום חלש');
  if (quality.qualityScore >= 80) strengths.push('איכות נכס גבוהה');
  else if (quality.qualityScore < 50) weaknesses.push('איכות נכס נמוכה');
  if (transport.score >= 13) strengths.push('סביבה עשירה בשירותים');

  if (Array.isArray(neighborhoodScore?.strengths)) {
    neighborhoodScore!.strengths!.forEach(s => strengths.push(s));
  }
  if (Array.isArray(neighborhoodScore?.weaknesses)) {
    neighborhoodScore!.weaknesses!.forEach(w => weaknesses.push(w));
  }

  void neighborhoodStats;

  return { totalScore, scoreLabel: getScoreLabel(totalScore), breakdown, qualityResult: quality, strengths, weaknesses, missingFields };
}
